import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Trash2, ShieldCheck, RefreshCw, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { InnerNav } from "../components/InnerNav";
import { useTranslate } from "../context/TranslateContext";
import { useMyCinema } from "../context/MyCinemaContext";
import { adminListUsers, adminUpdateUser, adminDeleteUser } from "../lib/api";

const ROLES = ["member", "editor", "admin"];
const roleBadge = {
  admin: "bg-gold text-ink", editor: "border border-gold/50 text-gold", member: "border border-edge text-ash",
};

export default function AdminUsers() {
  const { t } = useTranslate();
  const { user: me, isAdmin } = useMyCinema();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminListUsers().then((d) => setItems(d.items || [])).catch(() => toast.error(t("Could not load users")))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { window.scrollTo(0, 0); load(); }, [load]);

  const changeRole = async (id, role) => {
    try { const u = await adminUpdateUser(id, { role }); setItems((xs) => xs.map((x) => x.id === id ? u : x)); toast.success(t("Role updated")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("Update failed")); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("Delete this user? This cannot be undone."))) return;
    try { await adminDeleteUser(id); setItems((xs) => xs.filter((x) => x.id !== id)); toast.success(t("User deleted")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("Delete failed")); }
  };

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-users-page">
      <InnerNav />
      <div className="mx-auto max-w-[1200px] px-6 py-12 md:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 font-body text-xs uppercase tracking-[0.28em] text-gold">
              <Users className="h-4 w-4" /> {t("User Management")}
            </div>
            <h1 className="mt-3 font-display text-4xl font-semibold text-cream">{t("Members & Staff")}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="users-refresh" onClick={load} className="inline-flex items-center gap-2 rounded-full border border-edge px-4 py-2 font-body text-sm text-ash hover:border-gold hover:text-gold">
              <RefreshCw className="h-4 w-4" /> {t("Refresh")}
            </button>
            <Link to="/admin/content" data-testid="go-content" className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-4 py-2 font-body text-sm text-gold hover:bg-gold/10">
              <FileText className="h-4 w-4" /> {t("Content & Moderation")}
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-gold" /></div>
        ) : (
          <div className="mt-8 overflow-x-auto rounded-2xl border border-edge">
            <table className="w-full min-w-[720px] border-collapse text-left">
              <thead>
                <tr className="bg-surface/60 font-body text-xs uppercase tracking-widest text-ash">
                  <th className="px-5 py-3">{t("Name")}</th>
                  <th className="px-5 py-3">{t("Email")}</th>
                  <th className="px-5 py-3">{t("Role")}</th>
                  <th className="px-5 py-3 text-right">{t("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <motion.tr key={u.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="border-b border-edge align-middle" data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-4 font-display text-lg text-cream">
                      {u.name || "—"} {me && u.id === me.id && <span className="ml-1 font-body text-xs text-gold">({t("you")})</span>}
                    </td>
                    <td className="px-5 py-4 font-body text-sm text-ash">{u.email}</td>
                    <td className="px-5 py-4">
                      {isAdmin ? (
                        <select data-testid={`role-select-${u.id}`} value={u.role} onChange={(e) => changeRole(u.id, e.target.value)}
                          className="rounded-lg border border-edge bg-surface px-3 py-1.5 font-body text-sm text-cream focus:border-gold focus:outline-none">
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      ) : (
                        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 font-body text-xs ${roleBadge[u.role] || ""}`}>
                          {u.role === "admin" && <ShieldCheck className="h-3 w-3" />}{u.role}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right">
                      {isAdmin && (
                        <button data-testid={`user-delete-${u.id}`} onClick={() => remove(u.id)} disabled={me && u.id === me.id}
                          className="inline-flex items-center gap-1.5 rounded-full border border-edge px-3 py-1.5 font-body text-sm text-ash transition-colors hover:border-red-400 hover:text-red-400 disabled:opacity-40">
                          <Trash2 className="h-3.5 w-3.5" /> {t("Delete")}
                        </button>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!isAdmin && <p className="mt-4 font-body text-sm text-ash">{t("Editors can view members; only admins can change roles or remove users.")}</p>}
      </div>
    </div>
  );
}
